import { makeAutoObservable } from "mobx";

import { getEkidenSdkClient, setCurrentToken } from "@src/api/sdk";
import type { Wallet } from "@src/services";
import { tokenManager } from "@src/services";

import type { RootStore } from ".";

export class TokenStore {
  rootAccountToken: string | null = null;
  tokensLoaded = false;

  private rootAccountTokenLastRequested = 0;
  isGeneratingRootToken = false;

  constructor(private readonly rootStore: RootStore) {
    makeAutoObservable(this);
  }

  async initialize() {
    await this.loadFromSession();
  }

  get token() {
    return this.rootAccountToken;
  }

  // ==================== SETTERS ====================
  setRootAccountToken = (token: string | null) => {
    this.rootAccountToken = token;
    // Keep the module-level token in sync so every newly created client
    // (e.g. after resetEkidenSdkClient) gets the token set automatically.
    setCurrentToken(token);
    if (token) {
      this.rootAccountTokenLastRequested = Date.now();
      getEkidenSdkClient().setTokens({ rest: token });
    }
  };

  // ==================== TOKEN GENERATION ====================
  generateRootAccountToken = async (wallet: Wallet, force = false) => {
    if (!wallet.account || this.isGeneratingRootToken) return;

    await tokenManager.generateRootAccountToken(
      wallet,
      {
        setRootAccountToken: this.setRootAccountToken,
        getTokensLoaded: () => this.tokensLoaded,
        getRootAccountToken: () => this.rootAccountToken,
        getRootAccountTokenLastRequested: () => this.rootAccountTokenLastRequested,
        getIsGeneratingRootToken: () => this.isGeneratingRootToken,
        setIsGeneratingRootToken: (v) => {
          this.isGeneratingRootToken = v;
        },
      },
      force,
    );
  };

  // ==================== SESSION ====================
  private loadFromSession = async () => {
    await tokenManager.loadTokensFromSession({
      setRootAccountToken: this.setRootAccountToken,
      setTokensLoaded: (v) => {
        this.tokensLoaded = v;
      },
      setRootAccountTokenLastRequested: (v) => {
        this.rootAccountTokenLastRequested = v;
      },
    });
  };

  // ==================== RESET ====================
  reset = () => {
    this.rootAccountToken = null;
    this.rootAccountTokenLastRequested = 0;
    this.tokensLoaded = false;
    this.isGeneratingRootToken = false;
  };
}
